import { getCSRFToken, fetchCSRFToken } from "./csrf";

const BASE_URL = "/api/v1";
const API_PREFIX_TO_STRIP = "http://staging-api.ailingua.net/api/v1";

async function request(method, endpoint, data = null) {
  let csrfToken = getCSRFToken();

  if (!csrfToken) {
    await fetchCSRFToken();
    csrfToken = getCSRFToken();
  }

  const headers = {
    "Content-Type": "application/json",
    "X-CSRFToken": csrfToken,
  };

  const options = {
    method,
    headers,
    credentials: "include",
  };

  if (data) options.body = JSON.stringify(data);

  const response = await fetch(`${BASE_URL}${endpoint}`, options);

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.message || errorData.detail || `${response.status}: ${response.statusText}`);
  }

  if (method === "DELETE" && response.status === 204) {
    return {};
  }

  return response.json();
}

export async function getAllVideoFavorites() {
  const results = [];
  let nextEndpoint = `/favorites/video-lessons/`;

  while (nextEndpoint) {
    const response = await request("GET", nextEndpoint);

    if (response.results?.length) {
      results.push(...response.results);
    }

    if (response.next) {
      const relativeUrl = response.next.replace(API_PREFIX_TO_STRIP, "");
      nextEndpoint = relativeUrl.startsWith("/") ? relativeUrl : `/${relativeUrl}`;
    } else {
      nextEndpoint = null;
    }
  }

  return { results };
}

export async function addVideoFavorite(videoLessonId) {
  const payload = { lesson: parseInt(videoLessonId, 10) };
  return request("POST", `/favorites/video-lessons/`, payload);
}

export async function removeVideoFavorite(favoriteId) {
  return request("DELETE", `/favorites/video-lessons/${favoriteId}/`);
}

export async function getFavoriteStatus(videoLessonId) {
  try {
    const { results } = await getAllVideoFavorites();
    const lessonId = parseInt(videoLessonId, 10);
    const favorite = results.find((fav) => Number(fav.lesson) === lessonId);

    if (favorite) {
      return { isFavorited: true, favoriteId: favorite.id };
    }

    return { isFavorited: false, favoriteId: null };
  } catch (error) {
    console.error("Error checking favorite status:", error);
    return { isFavorited: false, favoriteId: null };
  }
}

export async function toggleVideoFavorite(videoLessonId, currentFavoriteId = null, isFavorited = false) {
  if (isFavorited) {
    if (currentFavoriteId) {
      return removeVideoFavorite(currentFavoriteId);
    }

    const { favoriteId } = await getFavoriteStatus(videoLessonId);
    if (favoriteId) {
      return removeVideoFavorite(favoriteId);
    }

    throw new Error("Could not find favorite record to remove");
  }

  try {
    return await addVideoFavorite(videoLessonId);
  } catch (error) {
    const { favoriteId } = await getFavoriteStatus(videoLessonId);
    if (favoriteId) {
      return { id: favoriteId, lesson: parseInt(videoLessonId, 10) };
    }
    throw error;
  }
}
